import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  HStack,
  Image,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  Checkbox,
  Link as ChakraLink,
  Spinner,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Divider,
} from '@chakra-ui/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FaLock, FaEnvelope } from 'react-icons/fa';
import config from '../config';
import axios from 'axios';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const validateForm = () => {
    const errors = {};
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Invalid email address';
    }
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/auth/login`, {
        email,
        password
      });
      
      // Extract user data properly from the response
      const userData = response.data.user || response.data;
      const userName = userData.name || userData.user?.name || "User";
      
      // Regular success flow
      const redirectPath = location.state?.from || (
        userData.role === 'admin' ? '/admin' :
        ['agent', 'sub-agent'].includes(userData.role) ? '/agent' : '/'
      );
      
      toast({
        title: 'Login successful',
        description: `Welcome back, ${userName}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      login(email, password, rememberMe);
      navigate(redirectPath);
    } catch (error) {
      // Check for our special pending approval error
      if (error.pendingApproval) {
        toast({
          title: 'Account Status',
          description: error.message,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        navigate('/pending-approval');
        return;
      }

      // Handle other errors
      let errorMessage = 'An error occurred during login';
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'Invalid email or password';
            break;
          case 404:
            errorMessage = 'Account not found';
            break;
          case 429:
            errorMessage = 'Too many login attempts. Please try again later';
            break;
          default:
            errorMessage = error.response.data?.message || 'Failed to login';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server. Please check your internet connection';
      }

      toast({
        title: 'Login Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <Box
      minH="calc(100vh - 64px)"
      bg={useColorModeValue('gray.50', 'gray.900')}
      py={12}
    >
      <Container maxW="md" py={8}>
        <VStack
          spacing={8}
          bg={bgColor}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          p={8}
          shadow="xl"
        >
          <VStack spacing={3} w="full">
            <Heading 
              size="lg"
              bgGradient="linear(to-r, brand.500, brand.600)"
              bgClip="text"
            >
              Welcome Back
            </Heading>
            <Text color={useColorModeValue('gray.600', 'gray.400')}>
              Sign in to your account
            </Text>
          </VStack>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={4} w="full">
              <FormControl isInvalid={formErrors.email}>
                <FormLabel htmlFor="email">Email</FormLabel>
                <InputGroup>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    disabled={isLoading}
                  />
                  <InputRightElement>
                    <FaEnvelope color="gray.300" />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{formErrors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={formErrors.password}>
                <FormLabel htmlFor="password">Password</FormLabel>
                <InputGroup>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    disabled={isLoading}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      icon={showPassword ? <ViewIcon /> : <ViewOffIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{formErrors.password}</FormErrorMessage>
              </FormControl>

              <HStack justify="space-between">
                <Checkbox
                  isChecked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                >
                  Remember me
                </Checkbox>
                <ChakraLink
                  as={Link}
                  to="/forgot-password"
                  color="brand.500"
                  fontWeight="semibold"
                  fontSize="sm"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Forgot password?
                </ChakraLink>
              </HStack>

              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                width="full"
                isLoading={isLoading}
                loadingText="Signing in..."
              >
                Sign In
              </Button>
            </VStack>
          </form>

          <VStack spacing={4} w="full" pt={4}>
            <Divider />

            <Text>
              Don't have an account?{' '}
              <ChakraLink
                as={Link}
                to="/register"
                color="brand.500"
                fontWeight="semibold"
                _hover={{ textDecoration: 'none', color: 'brand.600' }}
              >
                Register here
              </ChakraLink>
            </Text>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default Login;
